

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTrainingCompletionModal from 'components/NewTrainingCompletionModal';

const TrainingCompletions = () => {
    const [completions, setCompletions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('training_completions');

    useEffect(() => {
        fetchCompletions();
    }, []);

    const fetchCompletions = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/training-completions/');
            setCompletions(response.data);
        } catch (error) {
            console.error('Error fetching training completions:', error);
            setSnackbar({ open: true, message: 'Error loading training completions', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveCompletion = async (payload) => {
        try {
            await axiosInstance.post('/training-completions/', payload);
            setSnackbar({ open: true, message: 'Training Completion created successfully', severity: 'success' });
            fetchCompletions();
        } catch (error) {
            console.error('Error saving training completion:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/training-completion/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Training Module",
            accessor: "training_module_id",
            width: "25%",
            Cell: ({ row }) => (
                <MDTypography
                    component="span"
                    onClick={() => handleRowClick(row.original)}
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                >
                    {row.original.training_module?.name || row.original.training_module_id}
                </MDTypography>
            )
        },
        {
            Header: "Guide",
            accessor: "guide_id",
            width: "15%",
            Cell: ({ row }) => row.original.guide?.first_name && row.original.guide?.last_name 
                ? `${row.original.guide.first_name} ${row.original.guide.last_name}` 
                : row.original.guide_id || '-'
        },
        {
            Header: "Supplier",
            accessor: "supplier_id",
            width: "15%",
            Cell: ({ row }) => row.original.supplier?.name || row.original.supplier_id || '-'
        },
        {
            Header: "Status",
            accessor: "completion_status",
            width: "15%",
            Cell: ({ value }) => value || 'Not Started'
        },
        {
            Header: "Completion Date",
            accessor: "completion_date",
            width: "15%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "15%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: completions,
    }), [completions]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Training Completions</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Completion
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : completions.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Training Completion" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTrainingCompletionModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveCompletion}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TrainingCompletions;

